import React from 'react'
import { t } from 'ttag'
import cx from 'classnames'
import { Box } from 'components/layout/Box/Box'
import { Modal } from 'components/shared/Modal/Modal'
import { TabsBar } from 'components/shared/TabsBar/TabsBar'
import { PersonalityFactors } from 'components/modals/TestsReportModal/tabs/PersonalityFactors/PersonalityFactors'
import { useJobSeekerResultsModalState } from './hooks'
import { JobblinkOverview } from './components/JobblinkOverview/JobblinkOverview'
import { GmaModal } from 'components/modals/GmaModal/GmaModal'
import { Button } from 'components/shared/Button/Button'
import {
  TAB_NAMES,
  getTabs,
  getJobSeekerPersonalityFactors,
  scoreRunLabel,
} from './model'

import styles from './JobSeekerResultsModal.module.scss'

export const JobSeekerResultsModal = ({
  onCancel,
  onClose,
  jobSeeker,
  isOpen,
}) => {
  const { state, handlers } = useJobSeekerResultsModalState({
    isOpen,
    jobSeeker,
  })

  const {
    talentData,
    isGmaOpen,
    gmaScore,
    showGma,
    showGmaToggle,
    language,
    candidateResponse,
    activeTab,
    personalityTab,
    selectedCandidates,
    scoreRuns,
    selectedRunId,
    isLoadingRun,
  } = state
  const {
    handleClose,
    handleConfirm,
    setGmaScore,
    setShowGma,
    handleTab,
    setPersonalityTab,
    setSelectedCandidates,
    setSelectedRunId,
  } = handlers

  const tabs = getTabs()
  const panelScrollRef = React.useRef(null)

  // Only shown when this candidate has been scored by more than one algorithm version,
  // which happens when an older result was recalculated rather than replaced.
  const versionSelector = scoreRuns?.length > 1 && (
    <div className={styles.versionRow}>
      {scoreRuns.map(run => {
        const isActive = run.is_primary
          ? !selectedRunId
          : selectedRunId === run.id
        return (
          <Button
            key={run.id}
            label={scoreRunLabel(run, language)}
            className={cx(styles.versionButton, {
              [styles.versionButtonActive]: isActive,
            })}
            variant={isActive ? 'primary filled' : 'outlined'}
            onClick={() => setSelectedRunId(run.is_primary ? null : run.id)}
          />
        )
      })}
      {selectedRunId && (
        <span className={styles.versionNote}>
          {t`Calculated by an earlier version of the algorithm and shown as it was recorded.`}
        </span>
      )}
    </div>
  )

  const overviewPanel = (
    <>
      {versionSelector}
      {isLoadingRun ? (
        <div className={styles.versionNote}>{t`Loading earlier result…`}</div>
      ) : (
        <JobblinkOverview
          jobSeeker={jobSeeker}
          candidateResponse={candidateResponse}
          talents={talentData}
          language={language}
          showGmaToggle={showGmaToggle && !selectedRunId}
          showGma={showGma}
          setShowGma={setShowGma}
        />
      )}
    </>
  )

  const personalityPanel = candidateResponse ? (
    <div className={styles.innerContentPersonality}>
      <PersonalityFactors
        align="stretch"
        className={styles.personalityFactors}
        factors={getJobSeekerPersonalityFactors()}
        language={language}
        scores={[candidateResponse.scores]}
        response={[candidateResponse]}
        personalityTab={personalityTab}
        setPersonalityTab={setPersonalityTab}
        selectedCandidates={selectedCandidates}
        setSelectedCandidates={setSelectedCandidates}
        candidates={[jobSeeker]}
        name={[candidateResponse.name]}
      />
    </div>
  ) : null

  return (
    <Modal
      cancelLabel={t`Close`}
      className={styles.modal}
      {...{
        onCancel,
        onClose,
        isOpen,
      }}
    >
      <Modal.Content className={styles.content}>
        <TabsBar
          className={styles.tabs}
          tabClassName={styles.tab}
          onClick={handleTab}
          {...{ activeTab, tabs }}
        />
        <Box
          ref={panelScrollRef}
          className={cx(styles.panel, {
            [styles.panelOverview]: activeTab === TAB_NAMES.OVERVIEW,
            [styles.panelPersonality]: activeTab === TAB_NAMES.PERSONALITY,
          })}
          direction="column"
        >
          {activeTab === TAB_NAMES.OVERVIEW ? overviewPanel : personalityPanel}
        </Box>
      </Modal.Content>

      <Modal.Footer className={styles.footer} />

      {isGmaOpen ? (
        <GmaModal
          onCancel={handleClose}
          onClose={handleClose}
          onConfirm={handleConfirm}
          jobSeeker={jobSeeker}
          gmaScore={gmaScore}
          setGmaScore={setGmaScore}
          isOpen
        />
      ) : null}
    </Modal>
  )
}
